import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, ArrowLeft, FileDown, RefreshCw, Sparkles } from 'lucide-react';
import {
  generateImportReport,
  getImportSession,
  type ImportSessionSummary,
} from '@/api/import-history';
import { ImportReportView } from '@/components/import-report/ImportReportView';
import { formatDateTime } from '@/lib/import-history-labels';

/**
 * Rapport complet d'un import, page dédiée et imprimable : l'export PDF passe
 * par la boîte d'impression du navigateur (le layout masque sidebar/header à
 * l'impression). `?print=1` déclenche l'impression dès le rapport prêt
 * (bouton « Télécharger en PDF » du wizard et de la page de détail).
 */
export function ImportReportPage() {
  const { code = '', sessionId = '' } = useParams<{ code: string; sessionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const id = Number(sessionId);

  const [session, setSession] = useState<ImportSessionSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const printedRef = useRef(false);

  // Charge la session ; génère le rapport au premier passage s'il n'existe pas.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let detail: ImportSessionSummary = await getImportSession(code, id);
        if (!detail.aiAnalysis) {
          setGenerating(true);
          detail = await generateImportReport(code, id);
        }
        if (!cancelled) setSession(detail);
      } catch {
        if (!cancelled) setError('Impossible de charger le rapport de cet import.');
      } finally {
        if (!cancelled) setGenerating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, id]);

  // ?print=1 : impression automatique dès que le rapport est affiché.
  useEffect(() => {
    if (!session?.aiAnalysis || printedRef.current || searchParams.get('print') !== '1') return;
    printedRef.current = true;
    const handle = setTimeout(() => window.print(), 400);
    return () => clearTimeout(handle);
  }, [session, searchParams]);

  const regenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      setSession(await generateImportReport(code, id));
    } catch {
      setError('La génération du rapport a échoué. Réessayez dans quelques instants.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 pb-16 print:max-w-none print:pb-0">
      {/* Barre d'actions (jamais imprimée) */}
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div>
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 mb-1 text-muted-foreground"
            onClick={() => navigate(`/schools/${code}/imports/${id}`)}
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Retour au détail de l'import
          </Button>
          <h1 className="text-2xl font-bold">Rapport d'import</h1>
          {session && (
            <p className="text-sm text-muted-foreground">
              École <span className="font-mono">{code}</span> — import du{' '}
              {formatDateTime(session.startedAt)}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={regenerate} disabled={generating}>
            <RefreshCw className={`mr-2 h-4 w-4 ${generating ? 'animate-spin' : ''}`} />
            Régénérer
          </Button>
          <Button onClick={() => window.print()} disabled={!session?.aiAnalysis}>
            <FileDown className="mr-2 h-4 w-4" />
            Télécharger en PDF
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="print:hidden">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {generating && !session?.aiAnalysis && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed py-16 text-center">
          <Sparkles className="mb-3 h-8 w-8 animate-pulse text-primary" />
          <p className="font-medium">Analyse de l'import en cours…</p>
          <p className="mt-1 text-sm text-muted-foreground">
            L'intelligence artificielle rédige le rapport à partir des chiffres exacts de
            l'import. Cela prend quelques secondes.
          </p>
        </div>
      )}

      {!session && !error && !generating && <Skeleton className="h-64 w-full" />}

      {session?.aiAnalysis && <ImportReportView session={session} />}
    </div>
  );
}
